import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { SignIn, SignUp, ConfirmSignUp, ForgotPassword, ProtectedRoute, Setup } from './ui/components/Login';
import { setPageTitle } from './utils/Utils';
import TwitterCallback from './ui/components/TwitterLogin/TwitterCallback';
import './App.scss';
import AuthLayout from './ui/layouts/auth/AuthLayout';

const MainLayout = React.lazy(() => import(/* webpackChunkName: "MainLayout" */ './ui/layouts/main/MainLayout'));
const InfluencerForm = React.lazy(
  () => import(/* webpackChunkName: "InfluencerForm" */ './ui/components/Influencers/InfluencerForm'),
);
const AdminDashboard = React.lazy(() => import(/* webpackChunkName: "AdminDashboard" */ './ui/views/admin/AdminDashboard'));
// const CompetitionAnalyticsView = React.lazy(
//   () => import(/* webpackChunkName: "CompetitionAnalyticsVieqw" */ './ui/views/analytics/competitionAnalytics'),
// );
const GiggConfirmSignUp = React.lazy(
  () => import(/* webpackChunkName: "GiggConfirmSignUp" */ './ui/components/GiggAuth/GiggConfirmSignUp'),
);
const GiggResetPassword = React.lazy(
  () => import(/* webpackChunkName: "GiggResetPassword" */ './ui/components/GiggAuth/GiggResetPassword'),
);
const OAuth = React.lazy(() => import(/* webpackChunkName: "OAuth" */ './ui/components/GiggAuth/OAuth'));
const IdeaChecker = React.lazy(() => import(/* webpackChunkName: "IdeaChecker" */ './ui/components/IdeaChecker'));
const MyEntities = React.lazy(() => import(/* webpackChunkName: "MyEntities" */ './ui/views/MyEntities'));
const EntityHome = React.lazy(() => import(/* webpackChunkName: "EntityHome" */ './ui/views/EntityHome'));
const Board = React.lazy(() => import(/* webpackChunkName: "Board" */ './ui/views/inbox/Board'));
const EntitySettings = React.lazy(() => import(/* webpackChunkName: "InboxSettings" */ './ui/views/EntitySettings'));
const EntityAnalytics = React.lazy(() => import(/* webpackChunkName: "BoardAnalyticsView" */ './ui/views/EntityAnalytics'));
const RoundsBreakdown = React.lazy(
  () => import(/* webpackChunkName: "InboxSettings" */ './ui/views/competition/RoundsBreakdown'),
);
const Settings = React.lazy(() => import(/* webpackChunkName: "Settings" */ './ui/views/settings/Settings'));
const AdminOrgPage = React.lazy(() => import(/* webpackChunkName: "AdminOrgPage" */ './ui/views/admin/AdminOrgPage'));
const AdminOrgsList = React.lazy(() => import(/* webpackChunkName: "AdminOrgsList" */ './ui/views/admin/AdminOrgsList'));
const AdminBoardsSummary = React.lazy(
  () => import(/* webpackChunkName: "AdminBoardsSummary" */ './ui/views/admin/AdminBoardsSummary'),
);
const AdminReports = React.lazy(() => import(/* webpackChunkName: "AdminReports" */ './ui/views/admin/AdminReports'));
const AdminLogsView = React.lazy(() => import(/* webpackChunkName: "AdminLogsView" */ './ui/views/admin/AdminLogsView'));
const AdminSystemSummary = React.lazy(
  () => import(/* webpackChunkName: "AdminSystemSummary" */ './ui/views/admin/AdminSystemSummary'),
);
const PublicSubmissions = React.lazy(
  () => import(/* webpackChunkName: "PublicSubmissions" */ './ui/views/publicSubmissions'),
);
export interface OrgParams {
  orgPath?: string;
  entityType?: string;
  entityId?: string;
  boardId?: string;
  [key: string]: string | undefined;
}

const App: React.FC = () => {
  useEffect(() => {
    setPageTitle();
  }, []);

  return (
    <>
      <div className="App">
        <Suspense fallback={<></>}>
          <Routes>
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<SignIn />} />
              <Route path="/register" element={<SignUp />} />
              <Route path="/forgot" element={<ForgotPassword />} />
            </Route>
            <Route path="/public">
              <Route path="verification" element={<GiggConfirmSignUp />} />
              <Route path="reset" element={<GiggResetPassword />} />
              <Route path="oauth-redirect" element={<OAuth />} />
              <Route path="idea" element={<IdeaChecker />} />
              <Route path="submission/:orgId/:inboxId" element={<PublicSubmissions />} />
            </Route>
            <Route path="/verification" element={<ConfirmSignUp />} />
            <Route path="/influencer" element={<InfluencerForm />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/twitter-callback" element={<TwitterCallback />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/admin" element={<AdminDashboard />}>
                <Route index element={<AdminSystemSummary />} />
                <Route path="orgs/:orgId" element={<AdminOrgPage />} />
                <Route path="orgs" element={<AdminOrgsList />} />
                <Route path="boards" element={<AdminBoardsSummary />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="logs" element={<AdminLogsView />} />
              </Route>
              <Route path="/" element={<MainLayout />}>
                <Route
                  path="/:orgPath/:entityType?"
                  element={
                    <Suspense fallback={<></>}>
                      <Outlet />
                    </Suspense>
                  }
                >
                  <Route index element={<MyEntities />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path=":entityId" element={<EntityHome />} />
                  <Route path=":entityId/board/:boardId" element={<Board />} />
                  <Route path=":entityId/analytics/:tab" element={<EntityAnalytics />} />
                  <Route path=":entityId/settings">
                    <Route index element={<EntitySettings />} />
                    <Route path=":tab/*" element={<EntitySettings />} />
                  </Route>
                  <Route path=":entityId/submissions">
                    <Route index element={<RoundsBreakdown />} />
                    <Route path=":stage/*" element={<RoundsBreakdown />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </div>
    </>
  );
};

export default App;
